// Non conformità — MOD 03 (T-251). Scheda di registro: registra le non
// conformità HACCP + azioni correttive. Sorgente: public.non_conformities
// (stessa tabella dell'export MOD 03 e del manuale → dati condivisi).

import { confirmDialog } from '../../lib/ui/dialogs.js';
import { toUserMessage } from '../../lib/errors.js';
import {
  IonBackButton, IonButton, IonButtons, IonContent, IonHeader, IonInput,
  IonItem, IonModal, IonPage, IonSelect, IonSelectOption, IonSpinner,
  IonTextarea, IonTitle, IonToast, IonToolbar,
} from '@ionic/react';
import { addOutline } from 'ionicons/icons';
import { IonIcon } from '@ionic/react';
import { useState } from 'react';

import { useAuth } from '../../lib/auth/AuthProvider.js';
import { useOfflineRegister, offlineRegisterStatus } from '../../lib/haccp/useOfflineRegister.js';

interface NcRow {
  id: string;
  rilevata_at: string;
  fase_processo: string;
  descrizione: string;
  gravita: string;
  tipo_pericolo: string;
  prodotto_coinvolto: string | null;
  lotto: string | null;
  azione_correttiva: string;
  responsabile_azione: string;
  data_chiusura: string | null;
  esito: string;
}

const FASE: Array<[string, string]> = [
  ['ricevimento', 'Ricevimento merci'], ['stoccaggio_refrigerato', 'Stoccaggio refrigerato'],
  ['stoccaggio_congelato', 'Stoccaggio congelato'], ['stoccaggio_secco', 'Stoccaggio secco'],
  ['scongelamento', 'Scongelamento'], ['pre_preparazione', 'Pre-preparazione'],
  ['preparazione_fredda', 'Preparazione fredda'], ['cottura', 'Cottura'],
  ['abbattimento', 'Abbattimento'], ['conservazione_cotti', 'Conservazione cotti'],
  ['riscaldo', 'Riscaldo'], ['somministrazione', 'Somministrazione'],
  ['conservazione_avanzi', 'Conservazione avanzi'], ['confezionamento_trasporto', 'Confezionamento/trasporto'],
  ['trasporto', 'Trasporto'], ['consegna', 'Consegna'], ['sanificazione', 'Sanificazione'],
  ['manutenzione', 'Manutenzione'], ['formazione', 'Formazione'], ['acquisto', 'Acquisto'],
  ['trasporto_fornitore', 'Trasporto fornitore'], ['altro', 'Altro'],
];
const GRAVITA: Array<[string, string]> = [['lieve', 'Lieve'], ['media', 'Media'], ['grave', 'Grave']];
const PERICOLO: Array<[string, string]> = [['B', 'Biologico'], ['C', 'Chimico'], ['F', 'Fisico'], ['A', 'Allergenico'], ['altro', 'Altro']];
const ESITO: Array<[string, string]> = [['aperta', 'Aperta'], ['in_verifica', 'In verifica'], ['chiusa', 'Chiusa']];

function todayIso(): string { return new Date().toISOString().slice(0, 10); }
function fmtDate(d: string | null | undefined): string {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d).slice(0, 10);
  return `${String(dt.getUTCDate()).padStart(2, '0')}/${String(dt.getUTCMonth() + 1).padStart(2, '0')}/${dt.getUTCFullYear()}`;
}
function labelOf(o: Array<[string, string]>, v: string): string { return o.find(([k]) => k === v)?.[1] ?? v; }
const ESITO_COLOR: Record<string, string> = { aperta: '#dc2626', in_verifica: '#ea7300', chiusa: '#16a34a' };

const EMPTY = {
  rilevata_at: todayIso(), fase_processo: 'ricevimento', descrizione: '', gravita: 'media',
  tipo_pericolo: 'B', prodotto_coinvolto: '', lotto: '', azione_correttiva: '',
  responsabile_azione: '', esito: 'aperta', data_chiusura: '',
};

export function NonConformitaPage(): JSX.Element {
  const { profile } = useAuth();
  const canEdit = profile?.is_platform_owner === true || profile?.role === 'consulente' || profile?.role === 'ristoratore';
  const [toast, setToast] = useState<string | null>(null);

  const { register, form, setForm, open, start, close, save: saveRecord, saving, ready,
    hasDraft, hasDraftChanges, discard: discardRecord, draftStatus, draftError, retryDraft } = useOfflineRegister<NcRow, typeof EMPTY>(
    'non_conformities', EMPTY, 'rilevata_at', canEdit,
  );
  const { data: rows = [], isLoading: loading, refetch: load } = register;

  async function discard(): Promise<void> {
    const remove = async () => {
      try { await discardRecord(); }
      catch (error) { setToast('Impossibile scartare la bozza: ' + toUserMessage(error)); }
    };
    if (!hasDraftChanges) { await remove(); return; }
    if (await confirmDialog('I dati della bozza saranno eliminati da questo dispositivo.', {
      header: 'Scartare la bozza?', okText: 'Scarta bozza', danger: true,
    })) await remove();
  }

  async function save(): Promise<void> {
    if (!profile?.active_tenant_id) { setToast('Nessun ristorante attivo'); return; }
    if (form.descrizione.trim().length < 5) { setToast('Descrizione troppo breve (min 5 caratteri)'); return; }
    if (form.azione_correttiva.trim().length < 5) { setToast('Azione correttiva troppo breve (min 5 caratteri)'); return; }
    if (!form.responsabile_azione.trim()) { setToast("Indica il responsabile dell'azione"); return; }
    try {
      const saved = await saveRecord({
        rilevata_at: new Date(form.rilevata_at + 'T12:00:00Z').toISOString(),
        fase_processo: form.fase_processo,
        descrizione: form.descrizione.trim(),
        gravita: form.gravita,
        tipo_pericolo: form.tipo_pericolo,
        prodotto_coinvolto: form.prodotto_coinvolto.trim() || null,
        lotto: form.lotto.trim() || null,
        azione_correttiva: form.azione_correttiva.trim(),
        responsabile_azione: form.responsabile_azione.trim(),
        esito: form.esito,
        data_chiusura: form.data_chiusura || null,
      });
      if (saved) setToast('Salvato sul dispositivo. In attesa di sincronizzazione.');
    } catch (error) {
      setToast('Salvataggio non riuscito: ' + toUserMessage(error));
    }
  }

  return (
    <IonPage>
      <IonHeader><IonToolbar>
        <IonButtons slot="start"><IonBackButton defaultHref="/haccp/registri-compila" /></IonButtons>
        <IonTitle>Non conformità</IonTitle>
      </IonToolbar></IonHeader>
      <IonContent className="ion-padding">
        <p role="status" style={{ color: '#64748b', fontSize: 13 }}>
          Le registrazioni si salvano sul dispositivo, anche senza rete, e si sincronizzano quando la connessione è disponibile.
        </p>
        {draftError && !open && <div role="alert"><p>{draftError}</p><IonButton onClick={retryDraft}>Riprova bozza</IonButton></div>}
        <p style={{ fontSize: 13, color: '#64748b', marginTop: 0 }}>
          Registro MOD 03: non conformità rilevate + azioni correttive. L'export A4 è in Export registri.
        </p>
        {canEdit && (
          <IonButton expand="block" disabled={!ready || saving} onClick={start} style={{ '--border-radius': '12px', marginBottom: 12 } as React.CSSProperties}>
            <IonIcon icon={addOutline} slot="start" /> {hasDraft ? 'Riprendi bozza' : 'Registra non conformità'}
          </IonButton>
        )}
        {register.isError && rows.length > 0 && (
          <div role="status"><p>Lettura non riuscita: sono mostrati gli ultimi dati caricati.</p><IonButton onClick={() => { void load(); }}>Riprova</IonButton></div>
        )}
        {loading ? <div style={{ textAlign: 'center', padding: 24 }}><IonSpinner /></div>
          : (register.isPending || register.isError) && rows.length === 0 ? (
          <div role="status" style={{ padding: 16 }}>
            <p>Impossibile leggere il registro sul dispositivo. Riprova.</p>
            <IonButton onClick={() => { void load(); }}>Riprova</IonButton>
          </div>
        ) : rows.length === 0 ? <p style={{ textAlign: 'center', color: '#94a3b8', padding: 24 }}>Nessuna non conformità disponibile su questo dispositivo.</p>
          : rows.map((r) => (
            <div key={r.id} style={{ background: '#fff', borderRadius: 12, padding: '12px 14px', marginBottom: 8, boxShadow: '0 1px 2px rgba(15,23,42,.06)' }}>
                {offlineRegisterStatus(r) && <p role="status" style={{ fontSize: 12, color: r._syncStatus === 'rejected' ? '#b91c1c' : '#92400e', margin: '0 0 6px' }}>{offlineRegisterStatus(r)}</p>}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <b style={{ fontSize: 14 }}>{labelOf(FASE, r.fase_processo)}</b>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: ESITO_COLOR[r.esito] ?? '#64748b', padding: '2px 8px', borderRadius: 999 }}>{labelOf(ESITO, r.esito)}</span>
              </div>
              <div style={{ fontSize: 12, color: '#334155', marginTop: 4 }}>{fmtDate(r.rilevata_at)} · {labelOf(GRAVITA, r.gravita)} · {labelOf(PERICOLO, r.tipo_pericolo)}</div>
              <div style={{ fontSize: 13, color: '#0f172a', marginTop: 4 }}>{r.descrizione}</div>
              {(r.prodotto_coinvolto || r.lotto) && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{[r.prodotto_coinvolto, r.lotto].filter(Boolean).join(' · lotto ')}</div>}
              <div style={{ fontSize: 12, color: '#334155', marginTop: 2 }}>Azione: {r.azione_correttiva} — {r.responsabile_azione}</div>
            </div>
          ))}

        <IonModal isOpen={open} canDismiss={!saving} onDidDismiss={close}>
          <IonHeader><IonToolbar>
            <IonTitle>Nuova non conformità</IonTitle>
            <IonButtons slot="end">
              <IonButton color="danger" disabled={saving || !ready} onClick={() => { void discard(); }}>Scarta bozza</IonButton>
              <IonButton disabled={saving} onClick={close}>Chiudi</IonButton>
            </IonButtons>
          </IonToolbar></IonHeader>
          <IonContent className="ion-padding">
            <p role="status" style={{ color: '#64748b', fontSize: 13 }}>
              {draftStatus}
            </p>
            {draftError && <div role="alert"><p>{draftError}</p><IonButton disabled={saving} onClick={retryDraft}>Riprova bozza</IonButton></div>}
            <IonItem><IonInput type="date" label="Data rilevazione *" labelPlacement="stacked" value={form.rilevata_at} onIonInput={(e) => setForm((f) => ({ ...f, rilevata_at: String(e.detail.value ?? '') }))} /></IonItem>
            <IonItem><IonSelect label="Fase processo *" labelPlacement="stacked" value={form.fase_processo} onIonChange={(e) => setForm((f) => ({ ...f, fase_processo: String(e.detail.value) }))}>{FASE.map(([k, v]) => <IonSelectOption key={k} value={k}>{v}</IonSelectOption>)}</IonSelect></IonItem>
            <IonItem><IonTextarea label="Descrizione *" labelPlacement="stacked" autoGrow value={form.descrizione} onIonInput={(e) => setForm((f) => ({ ...f, descrizione: String(e.detail.value ?? '') }))} /></IonItem>
            <IonItem><IonSelect label="Gravità *" labelPlacement="stacked" value={form.gravita} onIonChange={(e) => setForm((f) => ({ ...f, gravita: String(e.detail.value) }))}>{GRAVITA.map(([k, v]) => <IonSelectOption key={k} value={k}>{v}</IonSelectOption>)}</IonSelect></IonItem>
            <IonItem><IonSelect label="Tipo pericolo *" labelPlacement="stacked" value={form.tipo_pericolo} onIonChange={(e) => setForm((f) => ({ ...f, tipo_pericolo: String(e.detail.value) }))}>{PERICOLO.map(([k, v]) => <IonSelectOption key={k} value={k}>{v}</IonSelectOption>)}</IonSelect></IonItem>
            <IonItem><IonInput label="Prodotto coinvolto" labelPlacement="stacked" value={form.prodotto_coinvolto} onIonInput={(e) => setForm((f) => ({ ...f, prodotto_coinvolto: String(e.detail.value ?? '') }))} /></IonItem>
            <IonItem><IonInput label="Lotto" labelPlacement="stacked" value={form.lotto} onIonInput={(e) => setForm((f) => ({ ...f, lotto: String(e.detail.value ?? '') }))} /></IonItem>
            <IonItem><IonTextarea label="Azione correttiva *" labelPlacement="stacked" autoGrow value={form.azione_correttiva} onIonInput={(e) => setForm((f) => ({ ...f, azione_correttiva: String(e.detail.value ?? '') }))} /></IonItem>
            <IonItem><IonInput label="Responsabile azione *" labelPlacement="stacked" value={form.responsabile_azione} onIonInput={(e) => setForm((f) => ({ ...f, responsabile_azione: String(e.detail.value ?? '') }))} /></IonItem>
            <IonItem><IonSelect label="Esito" labelPlacement="stacked" value={form.esito} onIonChange={(e) => setForm((f) => ({ ...f, esito: String(e.detail.value) }))}>{ESITO.map(([k, v]) => <IonSelectOption key={k} value={k}>{v}</IonSelectOption>)}</IonSelect></IonItem>
            <IonItem><IonInput type="date" label="Data chiusura" labelPlacement="stacked" value={form.data_chiusura} onIonInput={(e) => setForm((f) => ({ ...f, data_chiusura: String(e.detail.value ?? '') }))} /></IonItem>
            <IonButton expand="block" disabled={saving || !ready} onClick={() => { void save(); }} style={{ marginTop: 16, '--border-radius': '12px' } as React.CSSProperties}>{saving ? <IonSpinner name="dots" /> : 'Salva'}</IonButton>
          </IonContent>
        </IonModal>
        <IonToast isOpen={!!toast} message={toast ?? ''} duration={2600} onDidDismiss={() => setToast(null)} />
      </IonContent>
    </IonPage>
  );
}
